import React, { useState, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { connectLean } from 'lean-redux';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import PopoverLabel from './PopoverLabel';
import { getAssignedSerializedClasses } from './utils';
import IDS from './__tests__/ids';
import TeacherPopover from './TeacherPopover';
import AdminCoordPopover from './AdminCoordPopover';

import './styles.scss';

const reducer = (state, action) => {
  const { type, payload } = action;
  let loading;
  let sections;

  switch (type) {
    case 'set-loading':
      ({ loading } = payload);
      return {
        ...state,
        loading,
      };
    case 'set-sections':
      ({ loading, sections } = payload);
      return {
        ...state,
        loading,
        sections,
      };
    default:
      throw Error('action type is not supported');
  }
};

/**
 * The teacher popover for seeing who watched the AP Daily videos.
 * */
const WatchedPopover = ({
  stats,
  totalStudents,
  watchedStudents: watchedStudentsPercentage,
  assignedSectionIds,
  isAdminOrCoordinator,
  subject,
  router,
}) => {
  const [openedSectionId, setOpenedSectionId] = useState(null);
  const [openedSchool, setOpenedSchool] = useState(null);
  const [serializedClasses, setSerializedClasses] = useState('');
  const [sectionsState, setSections] = useReducer(reducer, {
    loading: true,
    sections: [],
  });
  const watchedPercentage =
    totalStudents > 0 && watchedStudentsPercentage > 0
      ? (watchedStudentsPercentage / totalStudents) * 100
      : 0;
  const { sections, loading } = sectionsState;

  useEffect(() => {
    setSections({
      type: 'set-loading',
      payload: {
        loading: true,
      },
    });
  }, []);

  useEffect(() => {
    //determine when stats were loaded
    if (totalStudents >= 0 || (stats || []).length) {
      let classes = (stats || []).map((stat) => stat.classProgress).flat();
      const sortedClasses = classes.sort((a, b) =>
        a.className.localeCompare(b.className),
      );
      setSections({
        type: 'set-sections',
        payload: {
          loading: false,
          sections: sortedClasses,
        },
      });
      setSerializedClasses(
        getAssignedSerializedClasses(sortedClasses, assignedSectionIds),
      );
    }
  }, [totalStudents, stats, assignedSectionIds]);

  const onPopoverToggle = (isPopoverOpened) =>
    isPopoverOpened &&
    (sections || []).length === 1 &&
    setOpenedSectionId(sections[0].classId);
  const toggleSectionStudentContent = (sectionId) => {
    const isSameSectionId = openedSectionId === sectionId;
    // still show the StudentsContainer even when there is no studentProgress
    const openedSectionIdToBeSet = isSameSectionId ? null : sectionId;
    setOpenedSectionId(openedSectionIdToBeSet);
  };

  const toggleSchoolSectionContent = (school) => {
    const foundSchool = stats.find((s) => s.school === school);
    const isSameSchool = openedSchool === foundSchool.school;
    setOpenedSchool(isSameSchool ? null : school);
  };

  const decimalsPercentage = watchedPercentage.toFixed(2);
  const percentage =
    decimalsPercentage > 0 && decimalsPercentage < 1
      ? decimalsPercentage
      : Math.round(watchedPercentage);
  const popoverLabel = <PopoverLabel percentage={percentage} />;

  const schoolStats =
    (stats && stats.slice().sort((a, b) => a.school.localeCompare(b.school))) ||
    [];
  const adminPopover = (
    <AdminCoordPopover
      loading={loading}
      toggleSchoolSectionContent={toggleSchoolSectionContent}
      sections={sections}
      openedSchool={openedSchool}
      assignedSectionIds={Array.from(
        sections.filter((section) => section.assigned),
        (section) => section.classId,
      )}
      schoolStats={schoolStats}
    />
  );

  return (
    <PopOver
      position="bottom-end"
      className="video-watched-popover mr-4"
      size="wide"
      onToggle={onPopoverToggle}
      trigger={popoverLabel}
    >
      <div
        className="video-watched-popover-container"
        data-test-id={IDS.WATCHED_POPOVER_CONTAINER}
      >
        {(isAdminOrCoordinator && adminPopover) || (
          <TeacherPopover
            loading={loading}
            subject={subject}
            router={router}
            toggleSectionStudentContent={toggleSectionStudentContent}
            sections={sections}
            serializedClasses={serializedClasses}
            openedSectionId={openedSectionId}
            assignedSectionIds={assignedSectionIds}
          />
        )}
      </div>
    </PopOver>
  );
};

WatchedPopover.displayName = 'WatchedPopover';

WatchedPopover.propTypes = {
  scope: PropTypes.array,
  totalStudents: PropTypes.number,
  watchedStudents: PropTypes.number,
  isAdminOrCoordinator: PropTypes.bool.isRequired,
  subject: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
};

WatchedPopover.defaultProps = {
  scope: [],
  totalStudents: null,
  watchedStudents: null,
};

export default connectLean({
  mapState({ savedClasses: assignedSectionIds }) {
    return {
      assignedSectionIds: (assignedSectionIds || []).map((id) =>
        Number.parseInt(id, 10),
      ),
    };
  },
})(WatchedPopover);
